import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Button,
  Dialog,
  DialogTrigger,
  DialogContent,
  Flex,
  Switch,
  Text,
  TagBadge,
} from '@chatsdet/ui';
import { ClockClockwise, EnvelopeSimple, GearSix, MicrosoftTeamsLogo, Play, SlackLogo } from '@phosphor-icons/react';
import {
  AlertDestinationFields,
  fieldsFromAlertConfig,
  useAlertDestinationFields,
} from '../../../../../features/test-suites/AlertDestinationEditor';
import { ScheduleSuiteModal } from '../../../../../features/test-suites/ScheduleSuiteModal';
import { useRunTestSuite, type ExternalAreaSettings } from '../../../../../features/test-suites';
import { useSetAreaSettings } from '../../../../../features/external-tests/useExternalRunActions';

const AREA_CHANNEL_BADGES = {
  email: { name: 'Email', icon: <EnvelopeSimple size={12} /> },
  slack: { name: 'Slack', icon: <SlackLogo size={12} /> },
  teams: { name: 'Microsoft Teams', icon: <MicrosoftTeamsLogo size={12} /> },
};

/**
 * Small at-a-glance markers for an area row: which alert channel it sends to
 * (if any) and whether triage runs separately for it — so a 53-area suite
 * (e.g. a customer suite with dozens of areas) shows which teams have set themselves up
 * without opening every row.
 */
export function AreaSettingsIndicators({ settings }: { settings: ExternalAreaSettings | undefined }) {
  if (!settings) return null;
  return (
    <Flex gap="xs" align="center">
      {settings.alertConfig.steps.map((step, index) => (
        <TagBadge
          // Steps have no stable id of their own.
          key={`${step.channel}-${index}`}
          name={AREA_CHANNEL_BADGES[step.channel].name}
          color="blue"
          size="sm"
          hideDot
          icon={AREA_CHANNEL_BADGES[step.channel].icon}
        />
      ))}
      {settings.triageEnabled && (
        <TagBadge name="Triage" color="purple" size="sm" hideDot icon={<GearSix size={12} />} />
      )}
    </Flex>
  );
}

/**
 * Per-area "Alerts & triage" dialog (CAP-ETS-011): an area's own alert
 * destination, its own AI-triage switch, and the run/schedule actions scoped
 * to just this area. Kept out of `AreasSection.tsx` to keep that file's size
 * in check — one row opens one of these.
 */
export function AreaSettingsDialog({
  suiteId,
  suiteName,
  area,
  settings,
  disabled,
}: {
  suiteId: string;
  suiteName: string;
  area: { path: string; label: string };
  settings: ExternalAreaSettings | undefined;
  /** Disabled areas can still be configured, but not run. */
  disabled: boolean;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const alertFields = useAlertDestinationFields(settings?.alertConfig ?? { steps: [] });
  const [triageEnabled, setTriageEnabled] = React.useState(settings?.triageEnabled ?? false);
  const [error, setError] = React.useState<string | null>(null);
  const setAreaSettings = useSetAreaSettings();
  const runSuite = useRunTestSuite();

  // A config with more than one step (e.g. Slack + email) can only have been
  // set from the CLI — the fields below only ever represent one step. Editing
  // and saving them here would silently drop every step but the first, so
  // instead show them read-only and never send `alertConfig` from this dialog.
  const multiStepConfig = (settings?.alertConfig.steps.length ?? 0) > 1;

  // What the fields were seeded with on open, so `save` sends `alertConfig`
  // only when the user changed it.
  const seededAlertFieldsRef = React.useRef({ channel: alertFields.channel, destination: alertFields.destination });

  // Re-hydrate from the latest saved settings each time the dialog opens,
  // rather than tracking `settings` live — an in-progress edit should not be
  // clobbered by a background refetch while the dialog is open.
  React.useEffect(() => {
    if (!open) return;
    const config = settings?.alertConfig ?? { steps: [] };
    alertFields.reset(config);
    seededAlertFieldsRef.current = fieldsFromAlertConfig(config);
    setTriageEnabled(settings?.triageEnabled ?? false);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // One save: the alert fields and the triage switch commit together, so a
  // typed destination can't be lost to a forgotten inner save.
  const alertFieldsChanged =
    alertFields.channel !== seededAlertFieldsRef.current.channel ||
    alertFields.destination !== seededAlertFieldsRef.current.destination;
  const save = () => {
    setError(null);
    setAreaSettings.mutate(
      {
        suiteId,
        area: area.path,
        ...(!multiStepConfig && alertFieldsChanged ? { alertConfig: alertFields.toAlertConfig() } : {}),
        triageEnabled,
      },
      {
        // Closing re-seeds the fields on the next open, so a later edit back to
        // the original value still counts as a change.
        onSuccess: () => setOpen(false),
        onError: (e) => setError(e instanceof Error ? e.message : 'Failed to save this area'),
      }
    );
  };

  const run = async () => {
    setError(null);
    try {
      const result = await runSuite.mutateAsync({ id: suiteId, externalAreas: [area.path] });
      if (result.kind === 'external') {
        navigate({ to: '/runs/external/$runId', params: { runId: result.runId } });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run this area');
    }
  };

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen} size="lg">
      <Button
        variant="icon"
        size="sm"
        iconOnly
        tooltip="Alerts & triage"
        iconLeft={<GearSix size={16} />}
      />
      <Dialog
        title={`Alerts & triage — ${area.label}`}
        eyebrow="Area settings"
        description="Send this area's own alert on completion, triage its failures separately, and run or schedule it on its own."
        footer={
          <>
            <ScheduleSuiteModal
              suiteId={suiteId}
              suiteName={suiteName}
              externalAreas={[area.path]}
              trigger={
                <Button variant="secondary" iconLeft={<ClockClockwise size={16} />}>
                  Schedule area
                </Button>
              }
            />
            <Button
              variant="secondary"
              iconLeft={<Play size={16} weight="fill" />}
              isDisabled={disabled || runSuite.isPending}
              isPending={runSuite.isPending}
              onPress={() => void run()}
            >
              Run area
            </Button>
            <Button
              variant="primary"
              isPending={setAreaSettings.isPending}
              onPress={save}
            >
              Save
            </Button>
          </>
        }
      >
        <DialogContent>
          <div className="rounded-lg border border-border bg-card p-3">
            <Text size="sm" weight="medium">
              Area alerts
            </Text>
            <Text size="xs" className="mt-1 text-muted-foreground">
              Send only when this area finishes, in addition to the suite's own alerts.
            </Text>
            <div className="mt-3 flex gap-2">
              <AlertDestinationFields
                channel={alertFields.channel}
                destination={alertFields.destination}
                onChannelChange={alertFields.setChannel}
                onDestinationChange={alertFields.setDestination}
                isDisabled={multiStepConfig}
              />
            </div>
            {multiStepConfig && (
              <Text size="xs" className="mt-2 text-muted-foreground text-pretty">
                This area alerts {settings?.alertConfig.steps.length} destinations, set from the
                CLI. Edit them there with{' '}
                <code className="bg-muted px-1 rounded">cofactor test-suite area</code>.
              </Text>
            )}
          </div>
          <div>
            <Switch
              isSelected={triageEnabled}
              onChange={setTriageEnabled}
              label="Triage this area's failures separately"
            />
            <Text size="xs" className="mt-1 text-muted-foreground text-pretty">
              When on, this area's alert includes its own AI triage of failures instead of relying
              on the suite-wide triage.
            </Text>
          </div>
          {error && (
            <Text size="sm" className="text-destructive">
              {error}
            </Text>
          )}
        </DialogContent>
      </Dialog>
    </DialogTrigger>
  );
}
